import React, { useState, useEffect, useContext, useCallback } from 'react'
import { View, StyleSheet, Platform, ScrollView, Linking, Modal, Pressable, Dimensions } from 'react-native'
import { Text, Icon, CheckBox, useTheme } from '@ui-kitten/components'
import { useColors } from '../utils/useColors'
import { sendFBAnalyticsEvent } from '../global/Firebase/firebaseAnalytics'
import { usePostHog } from 'posthog-react-native'
import * as Notifications from 'expo-notifications'
import { useTranslation } from 'react-i18next'
import { useNavigation } from '@react-navigation/native'
import { useAtomValue, useSetAtom } from 'jotai'
import { onboardingTasksLeftAtom, organizationAtom, userAtom, userLocaleAtom } from '../global/AtomsProvider'
import { updateOnboardingChecklist, completeOnboarding } from '../global/Firebase/firebase'
import SupportChatPanel from '../screens/Support/SupportChatPanel'
import { shouldUseNativeSupportChat } from '../global/Support/supportFlag'
import { supportUnreadAtom, useSupportUnreadWatcher, supportChatOpenRequestAtom } from '../global/Support/supportUnread'
import { useSupportMessageToast } from '../global/Support/supportMessageToast'
import { supportApi } from '../global/Support/supportApi'
import { quickReplyNavigation } from '../global/Support/quickReplies'
import * as Sentry from '@sentry/react-native'

// Lets the deeply-nested help sections open the embedded chat view without prop-drilling.
const SupportChatContext = React.createContext({ openChat: () => {} })

// Configuration
const getMenuItems = (t, userLocale, user, organization, openUrl) => [
  {
    title: t('helpCenter.instructionsFor' + organization?.businessType?.id),
    onPress: () => {
      openUrl('https://atlas-app.notion.site/9066a8ff92cc4678bd7ff5eab0ed0d7a?v=17bbada979914b48a15a9264df4f4b05')
      sendFBAnalyticsEvent('help_instructions')
    },
  },
  {
    title: t('helpCenter.whatIsNew'),
    onPress: () => {
      const url = userLocale === 'ru'
        ? 'https://atlas-app.notion.site/1dc64b32d20a4bc1bb226d9def6295ca'
        : 'https://atlas-app.notion.site/29c7ceb1e04f8002a32bfafcd23018a1'
      openUrl(url)
      sendFBAnalyticsEvent('what_is_new')
    },
  },
  {
    title: t('helpCenter.AtlasWeb'),
    onPress: () => openUrl('https://web.atlas-app.org'),
  },
]

// Badge Component
const NotificationBadge = ({ count }) => {
  if (!count || count === 0) return null
  
  return (
    <View style={styles.badge}>
      <Text style={styles.badgeText}>{count}</Text>
    </View>
  )
}

// Floating Button Component
const FloatingButton = ({ showModal, onPress, onboardingTasksLeft, primaryColor, primaryPressedColor }) => (
  <View style={styles.floatingButtonContainer} pointerEvents="box-none">
    <Pressable
      style={({ pressed }) => [
        styles.floatingButton,
        { backgroundColor: pressed ? primaryPressedColor : primaryColor },
        pressed && styles.pressed
      ]}
      onPress={onPress}
      hitSlop={styles.hitSlop}
      accessible={true}
      accessibilityRole="button"
      accessibilityLabel={showModal ? "Close help" : "Open help"}
    >
      <NotificationBadge count={onboardingTasksLeft} />
      <Icon 
        name={showModal ? 'close' : 'question-mark-circle'} 
        width={28} 
        height={28} 
        fill='#fff' 
      />
    </Pressable>
  </View>
)

/**
 * @param productScreenAvailable whether the `ProductDetail` route is mounted beside this button. The
 *   blocked-app branch of AppStack renders no navigator, so a support agent's product draft must not
 *   offer to open a screen that is not there (ATL-1323). The welcome's quick-reply buttons
 *   (ACT-QUICK-REPLY-001: ProductDetail, NewSale) follow the same flag, for the same reason.
 */
export const FloatingButtonWithModal = React.memo(function FloatingButtonWithModal({ productScreenAvailable = false }) {
  const theme = useTheme()
  const navigation = useNavigation()
  const onboardingTasksLeft = useAtomValue(onboardingTasksLeftAtom)
  const currentUser = useAtomValue(userAtom)
  // DR-2: watch for agent replies while the chat is closed and badge the floating button.
  // The user id is passed so the watcher can tell an account SWITCH from a remount: the unread
  // atoms (including the toast's baseline) are module state that survives a sign-out, and the
  // next merchant to sign in on this till must not inherit them.
  useSupportUnreadWatcher(currentUser?.id)
  // ATL-1343: announce a new message with a preview, not just a number on this button.
  // It belongs here rather than higher up: this component IS the tap target the toast opens, so
  // where it is not mounted there is nothing for a toast to lead to.
  useSupportMessageToast()
  const supportUnread = useAtomValue(supportUnreadAtom)
  const chatOpenRequest = useAtomValue(supportChatOpenRequestAtom)
  const setChatOpenRequest = useSetAtom(supportChatOpenRequestAtom)
  const floatingBadge =
    onboardingTasksLeft + (shouldUseNativeSupportChat() ? supportUnread : 0)
  const [showModal, setShowModal] = useState(false)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [view, setView] = useState('help') // 'help' | 'chat'
  const [screenData, setScreenData] = useState(Dimensions.get('window'))

  useEffect(() => {
    const onChange = (result) => {
      setScreenData(result.window)
    }
    const subscription = Dimensions.addEventListener('change', onChange)
    return () => subscription?.remove()
  }, [])

  // Push deep-link: when a support push notification is tapped, AppStack sets
  // supportChatOpenRequestAtom to true. Open the modal straight to the chat view
  // and reset the flag. Guarded by the native-chat kill-switch. Safe to call on
  // web — push taps don't reach this path on web but the guard is here for completeness.
  useEffect(() => {
    if (!chatOpenRequest) return
    setChatOpenRequest(false)
    if (!shouldUseNativeSupportChat()) return
    setView('chat')
    setIsModalVisible(true)
    setShowModal(true)
  }, [chatOpenRequest, setChatOpenRequest])

  const openModal = () => {
    setView('help')
    setIsModalVisible(true)
    setShowModal(true)
  }

  const closeModal = () => {
    setIsModalVisible(false)
    setShowModal(false)
    setView('help')
  }

  const openChat = () => setView('chat')

  // ATL-1323: an agent-drafted product opens the ordinary product screen in create mode. The chat is
  // a modal above the navigator, so it closes first; reopening the chat reloads the card, which then
  // shows whatever the customer saved.
  const openProductDraft = useCallback(
    (supportDraft) => {
      setIsModalVisible(false)
      setShowModal(false)
      setView('help')
      // Keyed per drafted item: a plain navigate to an already-focused ProductDetail would only swap
      // its params, and the form (seeded once, at mount) would keep showing the previous draft.
      navigation.navigate({
        name: 'ProductDetail',
        key: `support-draft-${supportDraft.actionId}-${supportDraft.item.index}`,
        params: { productItemProp: null, supportDraft },
      })
    },
    [navigation]
  )

  // ACT-QUICK-REPLY-001: a welcome quick-reply button opens the screen it names, like the draft
  // above (the chat closes first). The tap is reported in the background and never holds up the
  // navigation: the merchant is mid-action, and a lost measurement costs less than a stalled tap.
  const openQuickReply = useCallback(
    ({ messageId, reply }) => {
      supportApi.recordWelcomeQuickReplyTap(messageId, reply).catch((error) => {
        Sentry.captureException(error, { tags: { feature: 'support-welcome-quick-reply', reply } })
      })
      setIsModalVisible(false)
      setShowModal(false)
      setView('help')
      navigation.navigate(quickReplyNavigation(reply))
    },
    [navigation]
  )

  const handleButtonPress = () => {
    if (showModal) {
      closeModal()
    } else {
      openModal()
    }
  }

  const isWebBigScreen = Platform.OS === 'web' && screenData.width >= 400
  const modalTransparent = isWebBigScreen

  const floatingButton = (
    <FloatingButton
      showModal={showModal}
      onPress={handleButtonPress}
      onboardingTasksLeft={floatingBadge}
      primaryColor={theme['color-primary-500']}
      primaryPressedColor={theme['color-primary-600']}
    />
  )

  return (
    <>
      <SupportChatContext.Provider value={{ openChat }}>
        <Modal
          animationType={Platform.OS !== 'web' ? "slide" : "fade"}
          transparent={modalTransparent}
          visible={isModalVisible}
          onRequestClose={closeModal}
          presentationStyle={Platform.OS !== 'web' ? 'pageSheet' : undefined}
          supportedOrientations={['portrait', 'landscape']}
          statusBarTranslucent={false}
        >
          <ModalContainer
            onClose={closeModal}
            isWebBigScreen={isWebBigScreen}
            view={view}
            onBack={() => setView('help')}
            onOpenProductDraft={productScreenAvailable ? openProductDraft : undefined}
            onQuickReply={productScreenAvailable ? openQuickReply : undefined}
          />
        </Modal>
      </SupportChatContext.Provider>
      {floatingButton}
    </>
  )
})

// Close Button Component
const CloseButton = ({ onPress }) => {
  const theme = useTheme()

  return (
    <Pressable
      style={({ pressed }) => [
        styles.closeButton,
        { backgroundColor: pressed ? theme['background-basic-color-1'] : theme['background-basic-color-2'] },
        pressed && styles.pressed
      ]}
      onPress={onPress}
      hitSlop={styles.hitSlop}
    >
      <Icon name='close' width={20} height={20} fill={theme['text-hint-color']} />
    </Pressable>
  )
}

const ModalContainer = ({ onClose, isWebBigScreen, view, onBack, onOpenProductDraft, onQuickReply }) => {
  const theme = useTheme()
  const content =
    view === 'chat' ? (
      <SupportChatPanel onBack={onBack} onOpenProductDraft={onOpenProductDraft} onQuickReply={onQuickReply} />
    ) : (
      <MainHelp />
    )

  if (isWebBigScreen) {
    return (
      <View style={styles.webModalOverlay}>
        <Pressable
          style={styles.backdrop}
          onPress={onClose}
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel="Close modal"
        />
        <View style={[styles.webModalContainer, { backgroundColor: theme['background-basic-color-1'] }]}>
          <CloseButton onPress={onClose} />
          {content}
        </View>
      </View>
    )
  }

  return (
    <View style={styles.modalContainer}>
      <CloseButton onPress={onClose} />
      {content}
    </View>
  )
}



// Header Component
const HelpHeader = () => {
  const { t } = useTranslation()
  
  return (
    <View style={styles.header}>
      <View style={styles.headerContent}>
        <View style={styles.avatar}>
          <Text style={styles.avatarText}>B</Text>
        </View>
        <View style={styles.headerTextContainer}>
          <Text style={styles.greeting}>
            {t('helpCenter.greeting')} 👋
          </Text>
          <Text style={styles.subtitle}>
            {t('helpCenter.howCanWeHelp')}
          </Text>
        </View>
      </View>
    </View>
  )
}

// Quick Action Button Component
const QuickActionButton = ({ icon, title, onPress, showBadge = false, badgeCount = 0 }) => {
  const colors = useColors()
  const theme = useTheme()

  return (
    <Pressable
      style={({ pressed }) => [
        styles.quickActionButton,
        { backgroundColor: theme['background-basic-color-1'], borderColor: colors.borderGrey },
        pressed && [styles.quickActionPressed, { backgroundColor: theme['background-basic-color-2'] }]
      ]}
      onPress={onPress}
    >
      <View style={styles.iconContainer}>
        <Icon name={icon} width={24} height={24} fill={colors.primary} style={styles.quickActionIcon} />
        {showBadge && (
          <View style={styles.quickActionBadge}>
            <Text style={styles.quickActionBadgeText}>
              {badgeCount > 9 ? '9+' : badgeCount}
            </Text>
          </View>
        )}
      </View>
      <Text style={[styles.quickActionTitle, { color: colors.black }]}>{title}</Text>
    </Pressable>
  )
}

// Quick Actions Section Component
const QuickActionsSection = () => {
  const { t } = useTranslation()
  const postHog = usePostHog()
  const supportUnread = useAtomValue(supportUnreadAtom)

  const nativeChat = shouldUseNativeSupportChat()
  const chatUnread = nativeChat ? supportUnread : 0
  const showUnreadIndicator = chatUnread > 0

  const openUrl = (url) => {
    try {
        Linking.openURL(url)
    } catch (error) {
      console.error('Error opening URL:', error)
    }
  }

  const { openChat } = useContext(SupportChatContext)
  const handleChatPress = () => {
    if (nativeChat) {
      openChat()
      sendFBAnalyticsEvent('native_support_chat_opened')
      postHog.capture('native_support_chat_opened')
      return
    }
    // Native chat is the only support channel now; if it's disabled via the
    // remote-config kill-switch, fall back to the web help center.
    openUrl('https://web.atlas-app.org')
    sendFBAnalyticsEvent('support_chat_fallback')
    postHog.capture('support_chat_fallback')
  }

  const handleHelpCenterPress = () => {
    openUrl('https://atlas-app.notion.site/745b5f835e9144ecb022d729e6b2e23b?v=d8f2555a75e044e5a18d6bbc7e0cb4b8')
    sendFBAnalyticsEvent('help_all')
    postHog.capture('help_center_viewed')
  }

  return (
    <View style={styles.quickActionsRow}>
      <QuickActionButton
        icon="message-circle"
        title={t('helpCenter.chat')}
        onPress={handleChatPress}
        showBadge={showUnreadIndicator}
        badgeCount={chatUnread}
      />
      <QuickActionButton
        icon="question-mark-circle"
        title={t('helpCenter.helpCenter')}
        onPress={handleHelpCenterPress}
      />
    </View>
  )
}

// Recent Conversations Component
const RecentConversationsCard = () => {
  const { t } = useTranslation()
  const postHog = usePostHog()
  const colors = useColors()
  const theme = useTheme()
  const supportUnread = useAtomValue(supportUnreadAtom)

  const nativeChat = shouldUseNativeSupportChat()
  const chatUnread = nativeChat ? supportUnread : 0
  const showUnreadIndicator = chatUnread > 0

  const { openChat } = useContext(SupportChatContext)
  const handlePress = () => {
    if (nativeChat) {
      openChat()
      sendFBAnalyticsEvent('native_support_chat_opened')
      postHog.capture('native_support_chat_opened')
      return
    }
    Linking.openURL('https://web.atlas-app.org')
  }

  return (
    <Pressable
      style={({ pressed }) => [
        styles.conversationCard,
        { backgroundColor: theme['background-basic-color-1'], borderColor: colors.borderGrey },
        pressed && [styles.cardPressed, { backgroundColor: theme['background-basic-color-2'] }]
      ]}
      onPress={handlePress}
    >
      <View style={styles.conversationCardContent}>
        <View style={[
          styles.conversationIcon,
          { backgroundColor: showUnreadIndicator ? '#FF3B30' : colors.primary }
        ]}>
          {showUnreadIndicator ? (
            <Text style={styles.conversationBadgeText}>
              {chatUnread > 9 ? '9+' : chatUnread}
            </Text>
          ) : (
            <Icon name='message-circle' width={14} height={14} fill='white' />
          )}
        </View>
        <View style={styles.conversationTextContainer}>
          <Text style={[styles.conversationTitle, { color: colors.black }]}>
            {nativeChat ? t('helpCenter.recentConversations') : t('helpCenter.recentUpdates')}
          </Text>
          <Text style={[styles.conversationSubtitle, { color: colors.mediumGrey }]}>
            {nativeChat
              ? (showUnreadIndicator
                  ? t('helpCenter.newMessagesAvailable', {count: chatUnread})
                  : t('helpCenter.viewPastConversations'))
              : t('helpCenter.checkLatestNews')
            }
          </Text>
        </View>
        <Icon name='chevron-right' width={16} height={16} fill={colors.mediumGrey} />
      </View>
    </Pressable>
  )
}

// Ask Question Section Component
const AskQuestionSection = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const postHog = usePostHog()
  const organization = useAtomValue(organizationAtom)
  const user = useAtomValue(userAtom)
  const userLocale = useAtomValue(userLocaleAtom)
  const colors = useColors()

  const openUrl = (url) => {
    try {
        Linking.openURL(url)
    } catch (error) {
      console.error('Error opening URL:', error)
    }
  }

  const menuItems = getMenuItems(t, userLocale, user, organization, openUrl)

  const { openChat } = useContext(SupportChatContext)
  const handleStartConversation = () => {
    if (shouldUseNativeSupportChat()) {
      openChat()
      sendFBAnalyticsEvent('native_support_chat_opened')
      postHog.capture('native_support_chat_opened')
      return
    }
    // Kill-switch fallback: native chat disabled → contact support via Atlas Web.
    menuItems[2]?.onPress()
    sendFBAnalyticsEvent('start_conversation_fallback')
    postHog.capture('start_conversation_fallback')
  }

  return (
    <View style={styles.askQuestionSection}>
      <Text style={[styles.sectionTitle, { color: colors.black }]}>
        {t('helpCenter.askQuestion')}
      </Text>
      <Text style={[styles.sectionSubtitle, { color: colors.mediumGrey }]}>
        {t('helpCenter.ourBotAndTeam')}
      </Text>

      <Pressable
        style={({ pressed }) => [
          styles.startConversationButton,
          { backgroundColor: pressed ? theme['color-primary-600'] : colors.primary },
          pressed && styles.pressed
        ]}
        onPress={handleStartConversation}
      >
        <Icon name='message-circle' width={20} height={20} fill='white' style={styles.buttonIcon} />
        <Text style={styles.startConversationText}>
          {t('helpCenter.startConversation')}
        </Text>
      </Pressable>
    </View>
  )
}

// Menu Items Component
const MenuItemsList = () => {
  const { t } = useTranslation()
  const organization = useAtomValue(organizationAtom)
  const user = useAtomValue(userAtom)
  const userLocale = useAtomValue(userLocaleAtom)
  const colors = useColors()
  const theme = useTheme()

  const openUrl = (url) => {
    try {
      Linking.openURL(url)
    } catch (error) {
      console.error('Error opening URL:', error)
    }
  }

  const menuItems = getMenuItems(t, userLocale, user, organization, openUrl)

  return (
    <>
      <View style={styles.menuHeader}>
        <Text style={[styles.menuTitle, { color: colors.black }]}>
          {t('helpCenter.moreOptions')}
        </Text>
      </View>
      <View style={styles.menuItemsContainer}>
        {menuItems.map((item, index) => (
          <Pressable
            key={index}
            style={({ pressed }) => [
              styles.menuItem,
              { backgroundColor: theme['background-basic-color-1'], borderColor: colors.borderGrey },
              pressed && [styles.menuItemPressed, { backgroundColor: theme['background-basic-color-2'] }]
            ]}
            onPress={item.onPress}
          >
            <Text style={[styles.menuItemText, { color: colors.black }]}>
              {item.title}
            </Text>
          </Pressable>
        ))}
      </View>
    </>
  )
}

const MainHelp = React.memo(function MainHelp() {
  const organization = useAtomValue(organizationAtom)
  const theme = useTheme()

  return (
    <ScrollView
      style={styles.scrollView}
      bounces={false}
      overScrollMode="never"
    >
      <HelpHeader />

      <View style={[styles.contentContainer, { backgroundColor: theme['background-basic-color-1'] }]}>
        <View style={styles.quickActionsContainer}>
          <QuickActionsSection />
          <RecentConversationsCard />
        </View>

        <AskQuestionSection />

        {(organization?.onboardingChecklist && !organization?.onboardingCompleted) && (
          <OnboardingChecklist />
        )}
        
        <MenuItemsList />
        
        <View style={styles.bottomSpacer} />
      </View>
    </ScrollView>
  )
})



// Progress Bar Component
const ProgressBar = ({ progress }) => {
  const colors = useColors()

  return (
    <View style={[styles.progressBarContainer, { borderColor: colors.primary }]}>
      <View style={[styles.progressBar, { width: `${progress}%`, backgroundColor: colors.primary }]} />
    </View>
  )
}

// Onboarding Task Item Component
const OnboardingTaskItem = ({ task, index, isOpen, onToggleComplete, onToggleExpand }) => {
  const { t } = useTranslation()
  const colors = useColors()

  return (
    <View style={styles.taskItem}>
      <CheckBox
        style={styles.checkbox}
        checked={task.completed}
        onChange={(checked) => checked && onToggleComplete(index)}
      />
      <View style={styles.taskContent}>
        <Text category='s1' onPress={() => onToggleExpand(task.id)}>
          {task.title}
        </Text>
        {isOpen && (
          <View style={styles.taskDetails}>
            <Text style={[styles.taskDescription, { color: colors.mediumGrey }]}>{task.description}</Text>
            <View style={styles.taskActions}>
              <Text
                style={[styles.taskAction, styles.taskActionFirst, { color: colors.primary }]}
                onPress={() => onToggleComplete(index)}
              >
                {t('helpCenter.done')}
              </Text>
              <Text
                style={[styles.taskAction, { color: colors.primary }]}
                onPress={() => onToggleComplete(index)}
              >
                {t('helpCenter.skip')}
              </Text>
            </View>
          </View>
        )}
      </View>
    </View>
  )
}

const OnboardingChecklist = React.memo(function OnboardingChecklist() {
  const organization = useAtomValue(organizationAtom)
  const user = useAtomValue(userAtom)
  const [openSteps, setOpenSteps] = useState({})
  const [tasksLeft, setTasksLeft] = useState(3)
  const { t } = useTranslation()
  const postHog = usePostHog()
  const colors = useColors()

  useEffect(() => {
    sendFBAnalyticsEvent('onboarding_checklist_viewed')
    postHog.capture('onboarding_viewed')
  }, [])
  
  useEffect(() => {
    if (!organization?.onboardingChecklist) return
    
    const newOpenSteps = {}
    organization.onboardingChecklist.forEach((item, index) => {
      if (index === 0) {
        newOpenSteps[item.id] = false
      } else {
        const prevCompleted = organization.onboardingChecklist[index - 1].completed
        const currentCompleted = item.completed
        newOpenSteps[item.id] = !currentCompleted && prevCompleted
      }
    })
    setOpenSteps(newOpenSteps)
  }, [organization?.onboardingChecklist])

  const checkHowManyLeft = (checklist) => {
    return checklist.filter(task => !task.completed).length
  }

  const markCompleted = (index) => {
    const task = organization?.onboardingChecklist[index]
    if (!task || task.completed) return

    const newOnboardingChecklist = [...organization.onboardingChecklist]
    newOnboardingChecklist[index].completed = true
    
    sendFBAnalyticsEvent('onboarding_checklist_step_complete', { step: task.title })
    postHog.capture('onboarding_step_completed', { step: task.title })
    
    const newOpenSteps = { ...openSteps }
    newOpenSteps[task.id] = false
    
    // Open next task if exists
    if (index < newOnboardingChecklist.length - 1) {
      newOpenSteps[newOnboardingChecklist[index + 1].id] = true
    }
    
    setOpenSteps(newOpenSteps)
    
    const newTasksLeft = checkHowManyLeft(newOnboardingChecklist)
    updateOnboardingChecklist(organization?.ref, newOnboardingChecklist, newTasksLeft)
    
    try {
      Notifications.setBadgeCountAsync(newTasksLeft)
    } catch (error) {
      console.log('Badge count error:', error)
    }
    
    if (newTasksLeft === 0) {
      completeOnboarding(organization?.ref)
      sendFBAnalyticsEvent('onboarding_checklist_fully_complete')
      // ASO-ATTR-001: `onboarding_completed` is NO LONGER captured here. It now has exactly one
      // owner — `growth-milestones.service.ts`, which emits it from
      // `public.organizations.onboarding_completed_at` with a deterministic event id and a
      // (organization, milestone) primary key behind it.
      //
      // Two reasons this line had to go rather than stay as a belt-and-braces second emit:
      //   * It fired ONLY on this code path. Any other way the flag reaches true — a support
      //     action, a future auto-complete, a second device finishing the last task — emitted
      //     nothing, so "reliably after the final required step" was not true of it.
      //   * Two uncoordinated owners of one event is the exact shape ASO-AN-001's staging QA
      //     caught on `postHog.reset()`: each is correct alone and only their COMPOSITION is
      //     wrong. Here the composition would be a double count on the one path that works.
      // The Firebase analytics event above is untouched — it is a different sink with its own
      // dashboards, and it is not the event this ticket standardises.
      //
      // ⚠️ THIS IS A BREAKING CHANGE FOR EXISTING POSTHOG INSIGHTS, and it is not merely a change
      // of owner. Anyone reading `onboarding_completed` should know all three differences before
      // the first funnel looks wrong:
      //   * TIMING — emitted by the nightly sweep, so up to ~24h after the fact rather than at the
      //     instant. Same-session funnels built on it will read as broken.
      //   * IDENTITY — `distinctId: organization:<firestoreId>` with `$process_person_profile:
      //     false`. It is a GROUP fact now and belongs to no person, so person-level funnels and
      //     cohorts on it go to zero. That is the correct shape (an organization completes
      //     onboarding, not a user) but it is not the old shape.
      //   * COMPLETENESS — it derives from `organizations.onboarding_completed_at` in Supabase,
      //     which arrives via `completeOnboarding`'s BEST-EFFORT dual-write. That write is an
      //     update, and the durable outbox only replays creates, so a failure is a Sentry event and
      //     nothing else — the milestone is then never emitted and
      //     `analytics.growth_milestone_gaps` cannot see it either (the boolean is missing too).
      //     The gap is bounded by dual-write parity, not by the milestone ledger.
    }
    
    setTasksLeft(newTasksLeft)
  }

  const expandRow = (id) => {
    setOpenSteps(prev => ({ ...prev, [id]: !prev[id] }))
  }

  if (!organization?.onboardingChecklist) return null

  const progress = 100 - (tasksLeft * 100 / organization.onboardingChecklist.length)

  return (
    <View style={styles.onboardingContainer}>
      <Text category='h6' style={[styles.onboardingTitle, { color: colors.black }]}>
        {t('helpCenter.gettingStarted')}
      </Text>
      <Text style={[styles.onboardingWelcome, { color: colors.black }]}>
        {t('helpCenter.welcomeMessage', { name: user.name })}
      </Text>
      
      <ProgressBar progress={progress} />
      
      {organization.onboardingChecklist.map((task, index) => (
        <OnboardingTaskItem
          key={task.id}
          task={task}
          index={index}
          isOpen={openSteps[task.id]}
          onToggleComplete={markCompleted}
          onToggleExpand={expandRow}
        />
      ))}
    </View>
  )
})



const styles = StyleSheet.create({
  // Floating Button Styles
  floatingButtonContainer: {
    position: 'absolute',
    bottom: 40,
    right: 30,
    zIndex: 1000,
  },
  floatingButton: {
    alignItems: 'center',
    justifyContent: 'center',
    width: 56,
    height: 56,
    borderRadius: 28,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 20,
  },
  badge: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: '#FF3B30',
    position: 'absolute',
    top: -3,
    right: -3,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: 'white',
  },
  badgeText: {
    color: 'white',
    fontSize: 11,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  pressed: {
    transform: [{ scale: 0.95 }],
  },
  hitSlop: {
    top: 15,
    bottom: 15,
    left: 15,
    right: 15,
  },

  // Modal Styles
  // No maxWidth here: on iOS this is the pageSheet content and must fill the sheet's full width.
  // Capping it (the old maxWidth:400) left-aligned the content on >400pt iPhones (Pro/Pro Max),
  // exposing a white strip of the modal background on the right. Web uses webModalContainer.
  modalContainer: {
    flex: 1,
  },
  webModalOverlay: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  webModalContainer: {
    width: 400,
    shadowColor: '#000',
    shadowOffset: { width: -2, height: 0 },
    shadowOpacity: 0.25,
    shadowRadius: 10,
    elevation: 10,
  },
  closeButton: {
    position: 'absolute',
    top: 20,
    right: 15,
    zIndex: 10,
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 22,
  },
  // Scroll View and Main Content
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    marginTop: -20,
    paddingTop: 20,
  },

  // Header Styles
  header: {
    backgroundColor: '#1B74E4',
    paddingTop: 50,
    paddingBottom: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    marginBottom: 40,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 15,
  },
  avatarText: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
  },
  headerTextContainer: {
    flex: 1,
  },
  greeting: {
    color: 'white',
    fontSize: 24,
    fontWeight: 'bold',
    lineHeight: 32,
    marginBottom: 4,
  },
  subtitle: {
              color: 'white',
              fontSize: 16,
              lineHeight: 22,
  },

  // Quick Actions Styles
  quickActionsContainer: {
          paddingHorizontal: 20,
         paddingBottom: 15,
  },
  quickActionsRow: {
           flexDirection: 'row',
           marginBottom: 20,
  },
  quickActionButton: {
               flex: 1,
               borderRadius: 12,
               padding: 16,
    marginHorizontal: 4,
               alignItems: 'center',
               borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
               shadowOpacity: 0.1,
               shadowRadius: 2,
               elevation: 2,
  },
  quickActionPressed: {
    transform: [{ scale: 0.98 }],
  },
  iconContainer: {
    position: 'relative',
  },
  quickActionIcon: {
    marginBottom: 8,
  },
  quickActionBadge: {
                    position: 'absolute',
                    top: -4,
                    right: -4,
                    backgroundColor: '#FF3B30',
                    borderRadius: 8,
                    minWidth: 16,
                    height: 16,
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderWidth: 1,
    borderColor: 'white',
  },
  quickActionBadgeText: {
                      color: 'white',
                      fontSize: 10,
                      fontWeight: 'bold',
  },
  quickActionTitle: {
               fontSize: 14,
               fontWeight: '500',
               textAlign: 'center',
  },

  // Conversation Card Styles
  conversationCard: {
             borderRadius: 12,
             marginBottom: 20,
             borderWidth: 1,
  },
  cardPressed: {
    transform: [{ scale: 0.98 }],
  },
  conversationCardContent: {
             flexDirection: 'row',
             alignItems: 'center',
             padding: 16,
  },
  conversationIcon: {
               width: 24,
               height: 24,
               borderRadius: 12,
               marginRight: 12,
               alignItems: 'center',
               justifyContent: 'center',
  },
  conversationBadgeText: {
                   color: 'white',
                   fontSize: 11,
                   fontWeight: 'bold',
  },
  conversationTextContainer: {
    flex: 1,
  },
  conversationTitle: {
                 fontSize: 14,
                 fontWeight: '500',
                 marginBottom: 2,
  },
  conversationSubtitle: {
                 fontSize: 12,
  },

  // Ask Question Section Styles
  askQuestionSection: {
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 5,
  },
  sectionSubtitle: {
           fontSize: 14,
           marginBottom: 15,
           lineHeight: 20,
  },
  startConversationButton: {
             borderRadius: 12,
             padding: 16,
             alignItems: 'center',
             flexDirection: 'row',
             justifyContent: 'center',
  },
  buttonIcon: {
    marginRight: 8,
  },
  startConversationText: {
             fontSize: 16,
             fontWeight: '600',
             color: 'white',
  },

  // Menu Styles
  menuHeader: {
    paddingHorizontal: 20,
  },
  menuTitle: {
          fontSize: 16,
    fontWeight: '600',
          marginTop: 20,
  },
  menuItemsContainer: {
    marginTop: 20,
    paddingHorizontal: 20,
  },
  menuItem: {
                borderRadius: 12,
                padding: 16,
                marginBottom: 8,
                borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
                shadowOpacity: 0.05,
                shadowRadius: 1,
                elevation: 1,
  },
  menuItemPressed: {
    transform: [{ scale: 0.98 }],
  },
  menuItemText: {
                fontSize: 15,
                fontWeight: '400',
  },

  // Onboarding Styles
  onboardingContainer: {
    marginHorizontal: 20,
    marginBottom: 10,
  },
  onboardingTitle: {
    marginTop: 0,
    marginBottom: 7,
    fontSize: 18,
    fontWeight: '600',
  },
  onboardingWelcome: {
    marginBottom: 10,
    fontSize: 14,
  },
  progressBarContainer: {
    width: '100%',
    height: 20,
    borderRadius: 7,
    overflow: 'hidden',
    padding: 1,
    borderWidth: 1,
    marginBottom: 13,
  },
  progressBar: {
    height: '100%',
    borderRadius: 6,
  },
  taskItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 10,
  },
  checkbox: {
    marginRight: 7,
  },
  taskContent: {
    flex: 1,
  },
  taskDetails: {
    marginTop: 5,
  },
  taskDescription: {
    fontSize: 14,
    marginBottom: 5,
  },
  taskActions: {
    flexDirection: 'row',
    marginTop: 3,
  },
  taskAction: {
    fontSize: 14,
    fontWeight: '500',
  },
  taskActionFirst: {
    marginRight: 10,
  },

  // Utility Styles
  bottomSpacer: {
    height: 20,
  },
})